import { Layers, Building2 } from 'lucide-react';
import { ViewLevel } from '../../contexts/ViewContext';
import { NODE_TYPE_LABELS } from '../orgchart/types';

interface DashboardHeaderProps {
  // CR-3384: View level info for display
  viewLevel?: ViewLevel;
  selectedGroupId?: number | null;
  viewLevelName?: string;
  /** CR-7411: the selected group is the top of its tree, so it is the Group,
   *  not a Division, even though its id is a real (> 0) group id. */
  isRootGroup?: boolean;
}

/**
 * The dashboard page header: title plus the current view-level indicator.
 *
 * CR-6401 moved the filter toggle, Refresh and Export report out of here into
 * DashboardFilterBar, which sits below the tab bar in the agreed layout.
 */
export function DashboardHeader({
  viewLevel,
  selectedGroupId,
  viewLevelName,
  isRootGroup,
}: DashboardHeaderProps) {
  // CR-3384: Get view level display info (uses configurable hierarchy labels)
  // selectedGroupId === 0 = synthetic root group, > 0 = division sub-group
  const getViewLevelDisplay = () => {
    if (!viewLevel) return null;

    switch (viewLevel) {
      case 'GROUP':
        // Distinguish root group (id=0) from division sub-groups (id>0)
        if (selectedGroupId && selectedGroupId > 0 && !isRootGroup) {
          return {
            icon: <Building2 className="h-4 w-4" />,
            label: NODE_TYPE_LABELS['division'],
            name: viewLevelName || 'All Companies',
            color: 'text-indigo-600 bg-indigo-100',
          };
        }
        return {
          icon: <Layers className="h-4 w-4" />,
          label: NODE_TYPE_LABELS['group'],
          name: viewLevelName || 'All Companies',
          color: 'text-purple-600 bg-purple-100',
        };
      case 'SITE':
        return {
          icon: <Building2 className="h-4 w-4" />,
          label: NODE_TYPE_LABELS['company'],
          name: viewLevelName || 'All Companies',
          color: 'text-blue-600 bg-blue-100',
        };
      case 'COMPANY':
      default:
        return {
          icon: <Building2 className="h-4 w-4" />,
          label: NODE_TYPE_LABELS['company'],
          name: viewLevelName || NODE_TYPE_LABELS['company'],
          color: 'text-blue-600 bg-blue-100',
        };
    }
  };

  const viewDisplay = getViewLevelDisplay();

  return (
    <div className="px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          {/* CR-3384: View level indicator */}
          {viewDisplay && (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${viewDisplay.color}`}>
              {viewDisplay.icon}
              <span>{viewDisplay.label}:</span>
              <span className="font-semibold">{viewDisplay.name}</span>
            </span>
          )}
        </div>
        {/* CR-6401: the filter toggle, Refresh and Export moved into
            DashboardFilterBar, which renders below the tab bar per the agreed
            layout. This row is now the page header alone. */}
      </div>
    </div>
  );
}
